import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useVisitStats } from "../../../hooks/useVisitStats";

const RANGES = [
  { label: "7 días", value: 7 },
  { label: "30 días", value: 30 },
  { label: "90 días", value: 90 },
];

/** Convierte una ruta interna en un nombre legible para el panel. */
function prettyPath(path: string): string {
  if (path === "/") return "Inicio";
  const known: Record<string, string> = {
    "/actualidad": "Actualidad",
    "/aviacion-rd": "Aviación RD",
    "/aeropuertos": "Aeropuertos",
    "/aerolineas": "Aerolíneas",
    "/estadisticas": "Estadísticas",
    "/estadisticas-aerolineas": "Estadísticas aerolíneas",
    "/estadisticas-aeropuertos": "Estadísticas aeropuertos",
    "/turismo": "Turismo",
    "/publicidad": "Publicidad",
    "/sobre-mi": "Sobre mí",
    "/contacto": "Contacto",
    "/archivo": "Archivo",
    "/articulo": "Noticias (todas)",
  };
  if (known[path]) return known[path];
  if (path.startsWith("/articulo/")) {
    const slug = path.replace("/articulo/", "");
    return `Noticia: ${slug.replace(/-/g, " ")}`;
  }
  return path;
}

function formatDay(day: string): string {
  // day viene como "YYYY-MM-DD"; se muestra "05 jul".
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return day;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("es-DO", { day: "2-digit", month: "short" });
}

export default function AnaliticasTab() {
  const [days, setDays] = useState(30);
  const { totals, uniques, daily, topPages, topArticles, loading, error, needsSetup, refetch } =
    useVisitStats(days);

  const chartData = useMemo(
    () => daily.map((d) => ({ label: formatDay(d.day), visits: d.visits })),
    [daily],
  );

  const maxPageVisits = Math.max(1, ...topPages.map((p) => p.visits));
  const maxArticleViews = Math.max(1, ...topArticles.map((a) => a.views));

  const cards = [
    {
      label: "Visitas hoy",
      value: totals.today,
      unique: uniques?.today,
      icon: "ri-calendar-check-line",
      color: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      label: "Últimos 7 días",
      value: totals.last_7_days,
      unique: uniques?.last_7_days,
      icon: "ri-calendar-line",
      color: "bg-emerald-50",
      iconColor: "text-emerald-600",
    },
    {
      label: "Últimos 30 días",
      value: totals.last_30_days,
      unique: uniques?.last_30_days,
      icon: "ri-calendar-2-line",
      color: "bg-amber-50",
      iconColor: "text-amber-600",
    },
    {
      label: "Visitas totales",
      value: totals.total,
      unique: uniques?.total,
      icon: "ri-eye-line",
      color: "bg-rose-50",
      iconColor: "text-rose-600",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2
            className="text-[#0B1F3A] font-extrabold text-2xl mb-1"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Analíticas del portal
          </h2>
          <p className="text-slate-500 text-sm">
            Visitas a tu página y las noticias más leídas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-slate-200 rounded-lg p-1">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  days === r.value
                    ? "bg-[#0B1F3A] text-white"
                    : "text-slate-500 hover:text-[#0B1F3A]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={refetch}
            className="w-9 h-9 flex items-center justify-center bg-white border border-slate-200 text-slate-500 rounded-lg hover:text-[#0B1F3A] hover:border-slate-300 transition-colors cursor-pointer"
            title="Actualizar"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Aviso: falta ejecutar el SQL en Supabase */}
      {needsSetup && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
          <div className="flex items-start gap-3">
            <i className="ri-error-warning-line text-amber-600 text-lg mt-0.5" />
            <div>
              <p className="text-amber-800 font-semibold text-sm mb-1">
                Falta un paso para activar las estadísticas
              </p>
              <p className="text-amber-700 text-xs leading-relaxed">
                Ejecuta el script{" "}
                <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono">
                  supabase/sql/analytics_setup.sql
                </code>{" "}
                en Supabase → SQL Editor. Una vez ejecutado, pulsa Actualizar y
                los datos empezarán a aparecer.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error genérico (no relacionado con el setup) */}
      {error && !needsSetup && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <p className="text-red-600 text-sm font-semibold">{error}</p>
        </div>
      )}

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-2xl border border-slate-100 p-5 text-center"
          >
            <div
              className={`w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-3 ${card.color}`}
            >
              <i className={`${card.icon} ${card.iconColor} text-xl`} />
            </div>
            <p
              className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {loading ? "…" : card.value.toLocaleString("es-DO")}
            </p>
            <p className="text-slate-400 text-xs leading-tight">{card.label}</p>
            {!loading && card.unique !== undefined && (
              <p className="text-slate-500 text-[11px] font-semibold mt-1.5">
                {card.unique.toLocaleString("es-DO")} visitantes únicos
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Gráfica de visitas por día */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h3
          className="text-[#0B1F3A] font-bold text-sm mb-4"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          <i className="ri-line-chart-line mr-1.5 text-blue-500" />
          Visitas por día
        </h3>
        {loading ? (
          <div className="h-64 bg-slate-50 rounded-xl animate-pulse" />
        ) : chartData.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-center">
            <i className="ri-bar-chart-box-line text-slate-300 text-4xl mb-2" />
            <p className="text-slate-400 text-sm">
              Aún no hay visitas registradas en este período.
            </p>
            <p className="text-slate-300 text-xs mt-1">
              Los datos aparecerán a medida que lleguen visitantes al sitio.
            </p>
          </div>
        ) : (
          <div style={{ width: "100%", height: 288 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="visitGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0B1F3A" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0B1F3A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e2e8f0" }}
                  minTickGap={16}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#0B1F3A", fontWeight: 700 }}
                  formatter={(value: number) => [value, "Visitas"]}
                />
                <Area
                  type="monotone"
                  dataKey="visits"
                  stroke="#0B1F3A"
                  strokeWidth={2}
                  fill="url(#visitGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Noticias más vistas */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3
              className="text-[#0B1F3A] font-bold text-sm"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              <i className="ri-fire-line mr-1.5 text-orange-500" />
              Noticias más vistas
            </h3>
          </div>
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : topArticles.length === 0 ? (
            <div className="text-center py-12 px-6">
              <i className="ri-article-line text-slate-300 text-3xl mb-2 block" />
              <p className="text-slate-400 text-sm">
                No hay lecturas en este período.
              </p>
              <p className="text-slate-300 text-xs mt-1">
                El contador sube cada vez que alguien abre una noticia.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {topArticles.map((a, idx) => (
                <div key={a.id} className="px-6 py-3">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-[10px] font-bold ${
                        idx < 3
                          ? "bg-orange-100 text-orange-600"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[#0B1F3A] text-xs font-semibold truncate"
                        style={{ fontFamily: "'Sora', sans-serif" }}
                        title={a.title}
                      >
                        {a.title}
                      </p>
                      <p className="text-slate-400 text-[10px]">{a.category}</p>
                    </div>
                    <span className="flex-shrink-0 flex items-center gap-1 text-slate-600 text-xs font-bold">
                      <i className="ri-eye-line text-slate-400" />
                      {a.views.toLocaleString("es-DO")}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-400 rounded-full"
                      style={{ width: `${(a.views / maxArticleViews) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Páginas más visitadas */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3
              className="text-[#0B1F3A] font-bold text-sm"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              <i className="ri-compass-3-line mr-1.5 text-blue-500" />
              Páginas más visitadas
            </h3>
          </div>
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : topPages.length === 0 ? (
            <div className="text-center py-12 px-6">
              <i className="ri-compass-3-line text-slate-300 text-3xl mb-2 block" />
              <p className="text-slate-400 text-sm">
                Aún no hay visitas en este período.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {topPages.map((p, idx) => (
                <div key={p.path} className="px-6 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-[10px] font-bold bg-slate-100 text-slate-500">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[#0B1F3A] text-xs font-semibold truncate"
                        title={p.path}
                      >
                        {prettyPath(p.path)}
                      </p>
                    </div>
                    <span className="flex-shrink-0 flex items-center gap-1 text-slate-600 text-xs font-bold">
                      <i className="ri-eye-line text-slate-400" />
                      {p.visits.toLocaleString("es-DO")}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-400 rounded-full"
                      style={{ width: `${(p.visits / maxPageVisits) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
